import React, { createContext, useState, useEffect, useContext } from 'react';
import socketServices from '../utility/socketService';
import Cookies from 'universal-cookie';
import { COMPANY_ID, COMPANY_EMPLOYEE_ID, TOKEN_NAME } from '../config';
import toast from 'react-hot-toast';
import ChatNotification from '../components/notifications/ChatNotification';
import { useNavigate, useLocation } from 'react-router-dom';

export const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const location = useLocation(); 
  
  const [isConnected, setIsConnected] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [chatList, setChatList] = useState([]);
  const [isInitialChatListFetched, setIsInitialChatListFetched] = useState(false);
  const [activeChatRoom, setActiveChatRoom] = useState(null);
  const [prevPathname, setPrevPathname] = useState(location.pathname);

  const hasRequiredCookies = () => {
    const token = cookies.get(TOKEN_NAME);
    const companyId = cookies.get(COMPANY_ID);
    const employeeId = cookies.get(COMPANY_EMPLOYEE_ID);
    return token && companyId && employeeId;
  };

  const initializeSocket = () => {
    if (hasInitialized || !hasRequiredCookies()) {
      return;
    }

    socketServices.initializeSocket();
    setHasInitialized(true);

    socketServices.on("connect", () => {
      console.log("Socket connected globally");
      setIsConnected(true);
      
      const companyId = Number(cookies.get(COMPANY_ID));
      const employeeId = Number(cookies.get(COMPANY_EMPLOYEE_ID));
      
      socketServices.emit(
        "connection-event",
        {
          self_employee_id: employeeId,
          company_id: companyId
        },
        (response) => {
          console.log("Connection event response:", response);
          if (response?.success) {
            console.log("User connected to socket server successfully");
          } else {
            console.error("Error connecting to socket server:", response);
          }
        }
      );
      
      setConnectionAttempts(0);
    });

    socketServices.on("disconnect", () => {
      console.log("Socket disconnected globally");
      setIsConnected(false);
    });

    socketServices.on("connect_error", (error) => {
      setConnectionAttempts(prev => prev + 1);
      
      if (connectionAttempts > 2) {
        toast.error("Having trouble connecting to the socket server. Please check your connection.");
      }
    });

    socketServices.on("chat-list", handleChatListUpdate);
  };

  const registerChatRoom = (chatId) => {
    if (chatId) {
      setActiveChatRoom(chatId);
    }
  };

  const unregisterChatRoom = (chatId) => {
    if (activeChatRoom === chatId) {
      setActiveChatRoom(null);
    }
  };

  const leaveActiveChatRoom = () => {
    if (activeChatRoom && isConnected) {
      socketServices.emit("leave-room", { chat_id: activeChatRoom });
      setActiveChatRoom(null);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const currentPath = window.location.pathname;
      if (currentPath === '/chats') {
        return;
      }
      
      if (activeChatRoom && isConnected) {
        console.log(`Navigation detected - leaving chat room ${activeChatRoom}`);
        socketServices.emit("leave-room", { chat_id: activeChatRoom });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [activeChatRoom, isConnected]);

  useEffect(() => {
    if (prevPathname === '/chats' && location.pathname !== '/chats' && activeChatRoom) {
      leaveActiveChatRoom();
    }
    
    setPrevPathname(location.pathname);
  }, [location.pathname, activeChatRoom, isConnected]);

  const handleChatListUpdate = (chatData) => {
    if (!chatData) return;

    const isOnChatPage = window.location.pathname === '/chats';
    
    setChatList(prevList => {
      const existingIndex = prevList.findIndex(chat => chat.chat_id === chatData.chat_id);
      
      let updatedList = [...prevList];
      
      if (existingIndex !== -1) {
        updatedList[existingIndex] = {
          ...updatedList[existingIndex],
          chat: chatData.chat,
          other_member: chatData.other_member.select
        };
      } else {
        updatedList.push({
          chat_id: chatData.chat_id,
          other_member_id: chatData.other_member_id,
          other_member: chatData.other_member.select,
          chat: chatData.chat
        });
      }
      
      return updatedList.sort((a, b) => {
        const timeA = new Date(a.chat?.last_message_at || 0);
        const timeB = new Date(b.chat?.last_message_at || 0);
        return timeB - timeA;
      });
    });
    
    if (!isOnChatPage) {
      const firstName = chatData.other_member.select.first_name;
      const lastName = chatData.other_member.select.last_name;
      
      const messageContent = chatData.chat.last_chat_message?.message || '';
      
      const senderAvatar = chatData.other_member.select.image_path || null;
      
      if (chatData.chat.last_chat_message?.msg_type === 'needs_attention') {
        ChatNotification({
          firstName: firstName,
          lastName: lastName,
          message: "Needs your attention!",
          avatar: senderAvatar,
          needsAttention: true,
          chatId: chatData.chat_id,
          onClickHandler: (url) => {
            if (url) {
              navigate(url);
            } else {
              navigate('/chats');
            }
          }
        });
      } else {
        ChatNotification({
          firstName: firstName,
          lastName: lastName,
          message: messageContent,
          avatar: senderAvatar,
          chatId: chatData.chat_id,
          onClickHandler: (url) => {
            if (url) {
              navigate(url);
            } else {
              navigate('/chats');
            }
          }
        });
      }
    }
  };

  const setInitialChatList = (initialData) => {
    if (initialData && Array.isArray(initialData) && !isInitialChatListFetched) {
      setChatList(initialData);
      setIsInitialChatListFetched(true);
    }
  };

  const disconnectSocket = () => {
    if (socketServices.socket) {
      if (activeChatRoom && isConnected) {
        socketServices.emit("leave-room", { chat_id: activeChatRoom });
        setActiveChatRoom(null);
      }
      
      socketServices.disconnect();
      setIsConnected(false);
      setHasInitialized(false);
    }
  };

  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      disconnectSocket();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const checkConnectionStatus = () => {
      const connected = socketServices.isConnected();
      if (connected !== isConnected) {
        setIsConnected(connected);
      }
    };
    
    const interval = setInterval(checkConnectionStatus, 3000);
    
    return () => clearInterval(interval);
  }, [isConnected]);

  const value = {
    isConnected,
    initializeSocket,
    disconnectSocket,
    chatList,
    setInitialChatList,
    registerChatRoom,
    unregisterChatRoom,
    leaveActiveChatRoom,
    emit: socketServices.emit.bind(socketServices),
    on: socketServices.on.bind(socketServices),
    removeListener: socketServices.removeListener.bind(socketServices),
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
